import { Close } from '@mui/icons-material';
import { Box, IconButton, Theme, Typography, useTheme } from '@mui/material';
import { useEffect, useEffectEvent, useState } from 'react';

import { Button } from '@/components';
import { YourNextListen } from '@/containers';
import { AnalyticsService, EVENT_CONSTANTS, EVENTS } from '@/services';
import {
  downloadCertificate,
  useAppDispatch,
  useFetchEpisodeCreditsQuery,
} from '@/store';
import { Assessment, Episode } from '@/types';

import { ScoreDonut } from './ScoreDonut';

interface OverlayMessageProps {
  score?: number;
  episode?: Episode;
  assessment?: Assessment;
  resetAssessment: () => void;
  navigateToEpisode: () => void;
}

const POLL_COUNTER_MAX = 10;

export const OverlayMessage = ({
  score,
  episode,
  assessment,
  resetAssessment,
  navigateToEpisode,
}: OverlayMessageProps) => {
  const theme = useTheme();
  const dispatch = useAppDispatch();

  const passRate = assessment?.passRate;
  const isPass =
    score === undefined ? undefined : passRate && score >= passRate;
  const credits = episode?.CPEDetails?.credits;

  // Poll against PD Hours table, once filename is set we know the cert is available
  const [certReady, setCertReady] = useState<boolean>(false);
  const [pollCounter, setPollCounter] = useState<number>(0);
  const { data: creditDetails, isFetching: isFetchingCreditDetails } =
    useFetchEpisodeCreditsQuery(
      { episodeId: episode?.episodeId ?? '' },
      {
        skip:
          !isPass ||
          !episode?.episodeId ||
          certReady ||
          pollCounter > POLL_COUNTER_MAX,
        pollingInterval: 1000,
      },
    );
  useEffect(() => {
    setCertReady(!!creditDetails?.fileName);
    if (isFetchingCreditDetails) {
      setPollCounter(prev => prev + 1);
    }
  }, [creditDetails, isFetchingCreditDetails]);

  const dismissOverlay = useEffectEvent((ev: KeyboardEvent) => {
    if (ev.key === 'Escape') {
      resetAssessment();
    }
  });

  // Allow dismiss via esc, for result overlays
  useEffect(() => {
    if (typeof isPass === 'boolean') {
      window.addEventListener('keydown', dismissOverlay);
    }

    return () => {
      window.removeEventListener('keydown', dismissOverlay);
    };
  }, [isPass]);

  if (!episode || !assessment) {
    return null;
  }

  const retakeAssessment = (component: string) => {
    AnalyticsService.logEvent(EVENTS.ASSESSMENT_EVENTS.RETAKE, {
      episode_id: episode.episodeId,
      assessment_id: assessment.userAssessmentId,
      total_questions: assessment.questions.length,
      score: score ?? 0,
      location: {
        component,
      },
    });
    resetAssessment();
  };

  if (isPass === true) {
    return (
      <Box sx={styles.overlay}>
        <IconButton
          aria-label="Close modal"
          onClick={resetAssessment}
          sx={styles.closeButton}>
          <Close />
        </IconButton>
        <Box sx={styles.overlayContent}>
          <ScoreDonut score={score ?? 0} color={theme.palette.green10} />
          <Typography variant="h1">Nice job, you passed!</Typography>
          <Typography variant="body1">
            Congratulations! With a score of {score ?? 0}%, you have earned{' '}
            <strong>
              {credits} CPE credit{credits === 1 ? '' : 's'}
            </strong>
            .{' '}
            {certReady
              ? 'Your certificate is now available in your tracker and is ready to download here.'
              : 'Your certificate will shortly be available to download here or in your Tracker.'}
          </Typography>
          <Box>
            {pollCounter > POLL_COUNTER_MAX && !certReady ? (
              <Typography variant="body1">
                Your certificate is not ready yet. Please check back later.
              </Typography>
            ) : (
              <Button
                fullWidth={false}
                skinny={false}
                isLoading={!certReady}
                variant="primary"
                label="Download Certificate"
                onClick={() => {
                  if (creditDetails) {
                    dispatch(
                      downloadCertificate({
                        certificateId: creditDetails?.certificateId,
                      }),
                    );
                  }
                }}
              />
            )}
          </Box>
          <Box sx={{ mt: 3 }}>
            <Typography variant="h4">
              Ready for your next listen? Check out these episodes:
            </Typography>
            <Box
              sx={{
                py: 2,
                mt: 1,
                display: 'block',
                overflowX: 'auto',
                maxWidth: '100%',
                textAlign: 'left',
              }}>
              <YourNextListen
                compact
                referrer={{ page: EVENT_CONSTANTS.PAGE.ASSESSMENT_COMPLETE }}
              />
            </Box>
          </Box>
        </Box>
      </Box>
    );
  }

  if (isPass === false) {
    return (
      <Box sx={styles.overlay}>
        <IconButton
          aria-label="Close modal"
          onClick={() => retakeAssessment('close_button')}
          sx={styles.closeButton}>
          <Close />
        </IconButton>
        <Box sx={styles.overlayContent}>
          <ScoreDonut score={score ?? 0} color={theme.palette.red} />
          <Typography variant="h1">You didn&apos;t pass this time.</Typography>
          <Typography variant="h4">
            You scored {score ?? 0}%, you need at least {passRate || 0}% to pass
            ‐ retake the assessment now or try again later. Don&apos;t worry,
            you have an unlimited amount of tries.
          </Typography>
          <Box
            sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 1 }}>
            <Button
              fullWidth={false}
              skinny={false}
              variant="secondary"
              label="Try again later"
              onClick={() => {
                resetAssessment();
                navigateToEpisode();
              }}
            />
            <Button
              fullWidth={false}
              skinny={false}
              variant="primary"
              label="Retake Assessment"
              onClick={() => {
                retakeAssessment('retake_button');
              }}
            />
          </Box>
        </Box>
      </Box>
    );
  }

  return null;
};

const styles = {
  overlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: (theme: Theme) => `${theme.palette.grey50}88`,
    zIndex: 10,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backdropFilter: 'blur(10px)',
    overflowY: 'auto',
  },
  overlayContent: {
    maxWidth: 685,
    textAlign: 'center',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 1.5,
    maxHeight: '100%',
    p: { xs: 2, md: 4 },
  },
  closeButton: {
    position: 'absolute',
    top: 16,
    right: 16,
  },
};
